import { login } from 'api/auth';
import { me } from 'api/node/users';
import { useTranslation } from 'react-i18next';
import { useAuth, useSnack } from 'hooks';
import { Navigate, useLocation } from 'react-router-dom';
import { camelCase } from 'lodash';
import { KoobLoginCard } from '@koob/ui';
import { KOOB_TO_URL } from '../../constants';

const TO_ROLES = ['to_admin', 'to_user'];

// The page that bounced us here, from router state or from the other app's
// login tab (`?from=`); a relative in-app path only, never an open redirect.
const returnPath = (location) => {
  const from =
    location.state?.from ?? new URLSearchParams(location.search).get('from');
  return from && from.startsWith('/') && !from.startsWith('//') ? from : null;
};

const Login = () => {
  const { t } = useTranslation(['auth', 'errors']);
  const { onUpdate, accessToken } = useAuth();
  const { notify } = useSnack();
  const location = useLocation();
  const from = returnPath(location);

  const handleLogin = async (values) => {
    try {
      const { data } = await login(values);

      // Two-step sign-in happens elsewhere: Koob admins sign in on the panel.
      if (data.mfaRequired) {
        if (data.signInUrl) window.location.assign(data.signInUrl);
        else notify(t('errors:mfaNotAvailable'), { type: 'error' });
        return;
      }

      // Temporarily store token so the axios interceptor can use it for the /user call
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);

      const { data: userData } = await me({ skip: false });

      if (TO_ROLES.includes(userData.role)) {
        // Clear tokens — this user belongs to the TO app
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');

        const params = new URLSearchParams({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          // The TO app finishes what this app was asked for (MCP consent).
          ...(from && { from }),
        });
        window.location.href = `${KOOB_TO_URL}/auth-redirect#${params}`;
        return;
      }

      onUpdate(data);
    } catch (err) {
      // Clear any temporarily stored tokens on error
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');

      const error = camelCase(
        err?.response?.data?.message ?? 'invalidCredential',
      );

      // A refused login reads as wrong credentials; any other unknown
      // message (server error, rate limit) as a general error.
      const fallback =
        err?.response?.status === 401
          ? 'errors:invalidCredential'
          : 'errors:error';
      notify(t(`errors:${error}`, { defaultValue: t(fallback) }), {
        type: 'error',
      });
    }
  };

  if (Boolean(accessToken)) {
    // Return to the page that bounced us here (deep links, MCP consent).
    return <Navigate to={from ?? '/'} />;
  }

  return (
    <KoobLoginCard
      onSubmit={handleLogin}
      forgotPasswordUrl="/lost-access"
      defaultTab="dmc"
      onTabChange={(tab) => {
        if (tab === 'to') {
          window.location.href = `${KOOB_TO_URL}/login${
            from ? `?from=${encodeURIComponent(from)}` : ''
          }`;
        }
      }}
      t={t}
    />
  );
};

export default Login;
